import React from 'react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';

const BiaxialScatterPlotSettings = ({
  chartTitle,
  onChartTitleChange,
  objectType,
  onObjectTypeChange,
  xAxisType,
  onXAxisTypeChange,
  xAxisValue,
  onXAxisValueChange,
  yAxisType,
  onYAxisTypeChange,
  yAxisValue,
  onYAxisValueChange,
  objectTypes,
  activitiesForType,
  dateAttrsForType,
  eventAttrNames,
  loading,
  activitiesLoading,
  dateAttrsLoading,
  localFilters,
  updateLocalFilter,
  activeFile,
}) => {
  const objectTypeList = Array.isArray(objectTypes)
    ? objectTypes
    : Object.keys(objectTypes || {});

  const renderAxisValueSelect = (axisType, axisValue, onAxisValueChange) => {
    const isDisabled = loading || !objectType;

    if (axisType === 'activity_timestamp') {
      return (
        <select
          value={axisValue}
          onChange={(e) => onAxisValueChange(e.target.value)}
          disabled={isDisabled || activitiesLoading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {!objectType ? (
            <option value="">Select object type first…</option>
          ) : activitiesLoading ? (
            <option value="">Loading activities…</option>
          ) : activitiesForType.length === 0 ? (
            <option value="">No activities found</option>
          ) : (
            <option value="">Select activity…</option>
          )}
          {activitiesForType.map((act) => (
            <option key={act} value={act}>{act}</option>
          ))}
        </select>
      );
    }

    if (axisType === 'event_attribute') {
      return (
        <select
          value={axisValue}
          onChange={(e) => onAxisValueChange(e.target.value)}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {eventAttrNames.length === 0 ? (
            <option value="">No event attributes found</option>
          ) : (
            <option value="">Select event attribute…</option>
          )}
          {eventAttrNames.map((attr) => (
            <option key={attr} value={attr}>{attr}</option>
          ))}
        </select>
      );
    }

    return (
      <select
        value={axisValue}
        onChange={(e) => onAxisValueChange(e.target.value)}
        disabled={isDisabled || dateAttrsLoading}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        {!objectType ? (
          <option value="">Select object type first…</option>
        ) : dateAttrsLoading ? (
          <option value="">Loading attributes…</option>
        ) : dateAttrsForType.length === 0 ? (
          <option value="">No date attributes found</option>
        ) : (
          <option value="">Select attribute…</option>
        )}
        {dateAttrsForType.map((attr) => (
          <option key={attr} value={attr}>{attr}</option>
        ))}
      </select>
    );
  };

  const axisLabel = (axisType) => {
    if (axisType === 'activity_timestamp') return 'Activity *';
    if (axisType === 'event_attribute') return 'Event Attribute *';
    return 'Attribute *';
  };

  return (
    <div className="space-y-6">
      {/* Chart Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Chart Title</label>
        <input
          type="text"
          value={chartTitle}
          onChange={(e) => onChartTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Biaxial Scatter Plot"
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Object Type *</label>
        <select
          value={objectType}
          onChange={(e) => onObjectTypeChange(e.target.value)}
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select object type…</option>
          {objectTypeList.map((ot) => (
            <option key={ot} value={ot}>{ot}</option>
          ))}
        </select>
      </div>

      {/* X-Axis */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-800">X-Axis</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
          <select
            value={xAxisType}
            onChange={(e) => onXAxisTypeChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object_attribute">Object Attribute</option>
            <option value="activity_timestamp">Activity Timestamp</option>
            <option value="event_attribute">Event Attribute</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {axisLabel(xAxisType)}
          </label>
          {renderAxisValueSelect(xAxisType, xAxisValue, onXAxisValueChange)}
        </div>
      </div>

      {/* Y-Axis */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-800">Y-Axis</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
          <select
            value={yAxisType}
            onChange={(e) => onYAxisTypeChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object_attribute">Object Attribute</option>
            <option value="activity_timestamp">Activity Timestamp</option>
            <option value="event_attribute">Event Attribute</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {axisLabel(yAxisType)}
          </label>
          {renderAxisValueSelect(yAxisType, yAxisValue, onYAxisValueChange)}
        </div>
      </div>

      <div className="border-t border-gray-200" />

      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['dateRange', 'objectTypes', 'objectAttributes', 'activityNodes']}
      />
    </div>
  );
};

export default BiaxialScatterPlotSettings;
